import { useLinkageAmount, useLinkageBusinessAPI } from '../LinkageCreationContext'
import CustomButton from '../../../../../ui/CustomButton'
import { useContext, useEffect, useMemo, useState } from 'react'
import GlobalContext from '../../../../../../../context/global-context'
import { Title } from '../../../../../ui'
import ResizableModal from '../../../../../ui/ResizableModal'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import Lottie from 'lottie-react'
import { Box } from '@mui/material'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import { sileo } from 'sileo'
import { TablesAPI } from '../../../../../../../api/table.api'
import dayjs from 'dayjs'
import { CentroCostosAPI } from '../../../../../../../api/centroCostos.api'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'

const CENTER_COLORS = [
  '#00C6B3',
  '#FF6B6B',
  '#4F46E5',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#A855F7',
  '#F97316',
  '#22C55E'
]

const buildCentroCostoPayload = rows => {
  return rows
    .filter(row => row.centroCostoId && row.porcentaje > 0)
    .map(row => ({
      idCentroCosto: Number(row.centroCostoId),
      porcentajeCentroCosto: row.porcentaje
    }))
}

const buildInitialCentroCostoRows = (count = 2) =>
  Array.from({ length: count }).map((_, index) => ({
    centroCostoId: '',
    porcentaje: 0,
    color: CENTER_COLORS[index % CENTER_COLORS.length]
  }))

const isForbiddenCentroName = name =>
  name?.trim().toLowerCase() === 'estado de resultados consolidado'.toLowerCase()

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans
        ${
          disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'focus:border-indigo-500 focus:ring-indigo-500'
        }
      `}
    />
  </div>
)

const SelectField = ({ label, value, onChange, options, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans ${
        disabled
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'focus:border-indigo-500 focus:ring-indigo-500'
      }`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
)

const TextAreaField = ({ label, value, onChange, ...props }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <textarea
      className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
      value={value}
      onChange={onChange}
      maxLength={50}
      required
      {...props}
    />
    <p className='text-xs text-gray-500 mt-1'>{value.length}/50 caracteres</p>
  </div>
)

const CustomSelect = ({ label, name, options, value, onChange, disabled }) => (
  <div className='relative flex flex-col items-start'>
    {label && (
      <label
        htmlFor={name}
        className='absolute top-[-15px] left-4 bg-transparent text-xs font-medium text-gray-500 font-sans'
      >
        {label}
      </label>
    )}
    <select
      className='h-9 w-full appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs font-sans'
      name={name}
      onChange={onChange}
      value={value}
      disabled={disabled}
    >
      <option value='' disabled hidden>
        Seleccione una opcion
      </option>
      {options.map(option => (
        <option key={option.id} value={option.id}>
          {option.nombreCuenta}
        </option>
      ))}
    </select>
  </div>
)

const CircleProgress = ({ rows }) => {
  const total = Math.min(
    100,
    rows.reduce((acc, row) => acc + (Number(row.porcentaje) || 0), 0)
  )

  let offset = 0
  const stops = []

  rows.forEach(row => {
    const pct = Math.max(0, Math.min(100 - offset, Number(row.porcentaje) || 0))
    if (pct <= 0) return

    const color = row.color || '#00C6B3'
    stops.push(`${color} ${offset}% ${offset + pct}%`)
    offset += pct
  })

  if (offset < 100) {
    stops.push(`#E5E7EB ${offset}% 100%`)
  }

  const background = `conic-gradient(${stops.join(', ')})`

  return (
    <div className='flex flex-col items-center mt-3'>
      <div
        className='w-20 h-20 rounded-full flex items-center justify-center'
        style={{ background }}
      >
        <span className='text-xs font-semibold'>{total}%</span>
      </div>
      <span className='mt-1 text-[11px] text-gray-500 text-center font-sans'>
        Distribucion % centros de costo
      </span>
    </div>
  )
}

const INVENTORY_ACCOUNT_TYPE = 'existensias'

const normalizeText = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

const toNumericId = value => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const getComparableCuentaId = value => {
  if (value === null || value === undefined || value === '') return null
  return toNumericId(value) ?? String(value)
}

const getCuentaOptionId = cuenta =>
  getComparableCuentaId(cuenta?.id_key) ??
  getComparableCuentaId(cuenta?.id) ??
  cuenta?.id_key ??
  cuenta?.id ??
  null

const getCuentaOptionLabel = cuenta => cuenta?.name || cuenta?.nombreCuenta || cuenta?.nameCuenta || ''

const getCuentaOptionCode = cuenta =>
  cuenta?.codigo ??
  cuenta?.codigoCuenta ??
  cuenta?.code ??
  cuenta?.codeCuenta ??
  cuenta?.codigoFinal ??
  cuenta?.numCuenta ??
  ''

const getCuentaApiPayloadId = cuenta =>
  getComparableCuentaId(cuenta?.id_key) ?? getCuentaOptionId(cuenta)

const getCuentaDataId = data =>
  getComparableCuentaId(
    data?.cuentaContableId ??
    data?.idCuenta ??
    data?.id_key ??
    data?.id
  )

const AjusteExistencia = ({
  open,
  setOpen,
  type = 'egreso',
  setSwitchDate,
  idLibro,
  fVenc,
  folio,
  action
}) => {
  const [movimientoSeleccionado] = useState(null)
  const [fecha, setFecha] = useState('')
  const [, setVencimiento] = useState('')
  const [glosa, setGlosa] = useState('')
  const [monto, setMonto] = useState('')
  const [monto1, setMonto1, bankAmountLocked] = useLinkageAmount('')
  const [monto2, setMonto2] = useState('')
  const [, setMonto3] = useState('')
  const [selectedExistenciaCuenta, setSelectedExistenciaCuenta] = useState('')
  const [rut, setRut] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [centroCostoError, setCentroCostoError] = useState('')
  const [initialCentroCostoPayload, setInitialCentroCostoPayload] = useState([])
  const [centroCostos, setCentroCostos] = useState([])
  const [centroCostoTemporalidad, setCentroCostoTemporalidad] = useState(0)
  const [initialCentroTemporalidad, setInitialCentroTemporalidad] = useState(0)
  const [centrosCostoRows, setCentrosCostoRows] = useState(() => buildInitialCentroCostoRows(2))
  const businessAPI = useLinkageBusinessAPI()
  const { currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()
  const [data, setData] = useState({})
  const [dataBalance, setDataBalance] = useState({})
  const [loading, setLoading] = useState(false)
  const [loadingInventoryAccounts, setLoadingInventoryAccounts] = useState(false)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [balanceError, setBalanceError] = useState('')
  const [hasInitializedInventoryAccount, setHasInitializedInventoryAccount] = useState(false)
  const [loadedLibroId, setLoadedLibroId] = useState(null)
  const [razonesS, setRazonesS] = useState([])
  const [inventoryAccounts, setInventoryAccounts] = useState([])
  const tablesAPI = useMemo(() => new TablesAPI(), [])
  const centroCostosAPI = useMemo(() => new CentroCostosAPI(), [])

  const flatBalanceData = useMemo(
    () => (Array.isArray(dataBalance) ? dataBalance.flat(Infinity).filter(Boolean) : []),
    [dataBalance]
  )

  const inventoryAccountOptions = useMemo(
    () =>
      inventoryAccounts.map(option => {
        const optionName = getCuentaOptionLabel(option)
        const optionCode = getCuentaOptionCode(option)
        const matchedAccount = flatBalanceData.find(item => {
          const sameCode = normalizeText(item?.numCuenta) === normalizeText(optionCode)
          const sameName = normalizeText(item?.nameCuenta) === normalizeText(optionName)
          return sameCode || sameName
        })
        const nameCuenta = matchedAccount?.nameCuenta || optionName
        const numCuenta = matchedAccount?.numCuenta || optionCode
        const label = [nameCuenta, numCuenta ? `(${numCuenta})` : ''].filter(Boolean).join(' ')

        return {
          ...option,
          nameCuenta,
          numCuenta,
          activo: Number(matchedAccount?.activo ?? 0) || 0,
          value: getCuentaOptionId(option) ?? numCuenta ?? nameCuenta,
          label
        }
      }),
    [flatBalanceData, inventoryAccounts]
  )

  const selectedInventoryAccount = useMemo(
    () => {
      if (!selectedExistenciaCuenta) return null

      return inventoryAccountOptions.find(
        option => getComparableCuentaId(option.value) === getComparableCuentaId(selectedExistenciaCuenta)
      ) || null
    },
    [inventoryAccountOptions, selectedExistenciaCuenta]
  )
  const selectedVinculacionLabel = getCuentaOptionLabel(selectedInventoryAccount)
  const selectedVinculacionPayloadId = getCuentaApiPayloadId(selectedInventoryAccount)

  const handleClose = () => setOpen(false)

  const getAvailableCentroCostosCount = () => {
    if (!Array.isArray(centroCostos)) return 0
    return centroCostos.filter(cc => !isForbiddenCentroName(cc.name)).length
  }

  const functionGetData = async () => {
    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(
        currentBusiness?.id,
        idLibro,
        type.trim()
      )
      setData(response.data)
      setLoadedLibroId(idLibro)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getRazones = async () => {
    try {
      const response = await organizationAPI.getRazonSocial(currentBusiness?.id)
      setRazonesS(response.data)
    } catch (error) {
      console.error(error)
    }
  }

  const getCentroCostos = async () => {
    if (!currentBusiness?.id) return

    try {
      const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
      setCentroCostos(response.data)
    } catch (error) {
      console.error('Error al cargar los centros de costo:', error)
    }
  }

  const getInventoryAccounts = async () => {
    if (!currentBusiness?.id) {
      setInventoryAccounts([])
      return
    }

    try {
      setLoadingInventoryAccounts(true)
      const response = await organizationAPI.getCuentasContables({
        businessId: currentBusiness.id,
        type: INVENTORY_ACCOUNT_TYPE
      })
      setInventoryAccounts(Array.isArray(response?.data) ? response.data : [])
    } catch (error) {
      console.error('Error al cargar cuentas de existencias:', error)
      setInventoryAccounts([])
    } finally {
      setLoadingInventoryAccounts(false)
    }
  }

  const getDataBalanceRango = async fechaSeleccionada => {
    if (!currentBusiness?.id) return
    const year = dayjs(fechaSeleccionada).year()
    const fechaInicio = `${year}-01-01`
    const fechaFin = fechaSeleccionada
    setLoadingBalance(true)
    setBalanceError('')
    try {
      const res = await tablesAPI.getDataBalance(currentBusiness.id, fechaInicio, fechaFin)
      setDataBalance(res?.data ?? [])
    } catch (error) {
      console.error(error)
      setDataBalance([])
      setBalanceError('No se pudo cargar el saldo de existencias. Cambia la fecha para reintentar.')
    } finally {
      setLoadingBalance(false)
    }
  }

  const getCentroCostoOptions = () => {
    if (!Array.isArray(centroCostos)) return []

    return centroCostos
      .filter(cc => !isForbiddenCentroName(cc.name))
      .map(cc => ({
        id: cc.id,
        nombreCuenta: cc.name
      }))
  }

  const handleCentroCostoRowChange = (index, field, value) => {
    setCentrosCostoRows(prev => {
      const clone = [...prev]

      if (field === 'porcentaje') {
        const parsed = value === '' ? '' : Math.max(0, Math.min(100, Number(value) || 0))
        clone[index] = {
          ...clone[index],
          porcentaje: parsed
        }
      } else {
        clone[index] = {
          ...clone[index],
          [field]: value
        }
      }

      return clone
    })

    setCentroCostoError('')
  }

  const addCentroCostoRow = () => {
    setCentrosCostoRows(prev => {
      const maxRows = getAvailableCentroCostosCount()

      if (maxRows > 0 && prev.length >= maxRows) {
        return prev
      }

      const nextIndex = prev.length
      return [
        ...prev,
        {
          centroCostoId: '',
          porcentaje: 0,
          color: CENTER_COLORS[nextIndex % CENTER_COLORS.length]
        }
      ]
    })

    setCentroCostoError('')
  }

  const removeCentroCostoRow = index => {
    setCentrosCostoRows(prev => {
      return prev.filter((_, rowIndex) => rowIndex !== index)
    })

    setCentroCostoError('')
  }

  const clearCentroCostoRows = () => {
    setCentrosCostoRows([])
    setCentroCostoError('')
  }

  const formatMonto = value => {
    if (!value) return ''
    const stringValue = String(value)
    const numericValue = stringValue.replace(/\D/g, '')
    if (!numericValue) return ''
    return new Intl.NumberFormat('es-CL').format(numericValue)
  }

  const handleDatos = async () => {
    if (!selectedVinculacionPayloadId) {
      sileo.error({
        title: 'Cuenta Obligatoria',
        description: 'Debes seleccionar una cuenta de existencias'
      })
      return
    }

    if (loadingBalance || balanceError) return

    if (!fecha || !monto || !glosa) {
      sileo.error({
        title: 'Campos Obligatorios',
        description: 'Todos los campos son obligatorios excepto folio'
      })
      return
    }

    const payload = {
      fecha,
      transaccionId: action === 'replicar' ? null : movimientoSeleccionado?.id,
      razonSocial,
      rut,
      rutId: null,
      nombreComprobante: selectedVinculacionLabel || null,
      nombreComprobanteId: null,
      glosa,
      respaldoId: null,
      monto: Number(monto1),
      cuentaContableId: selectedVinculacionPayloadId,
      extra: {
        cuentaInventarioCodigo:
          selectedInventoryAccount?.numCuenta || getCuentaOptionCode(selectedInventoryAccount),
        cuentaInventarioNombre: selectedVinculacionLabel || selectedInventoryAccount?.nameCuenta || '',
        cuentaInventarioMonto: Number(monto || 0),
        monto1,
        monto2
      }
    }

    try {
      setLoading(true)
      const response = await businessAPI.postPagoArriendo(
        currentBusiness.id,
        payload,
        'ajuste-existencias'
      )
      console.info(response)
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error('Error al agregar comprobante:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedVinculacionPayloadId) {
      sileo.error({
        title: 'Cuenta Obligatoria',
        description: 'Debes seleccionar una cuenta de existencias'
      })
      return
    }

    const centroCostoPayload = buildCentroCostoPayload(centrosCostoRows)
    const temporalidad = Number(centroCostoTemporalidad ?? 0) || 0
    const hasCentroCostoChanges =
      JSON.stringify(centroCostoPayload) !== JSON.stringify(initialCentroCostoPayload) ||
      temporalidad !== initialCentroTemporalidad

    setCentroCostoError('')

    if (hasCentroCostoChanges && centroCostoPayload.length > 0) {
      const totalPorcentaje = centroCostoPayload.reduce(
        (acc, item) => acc + (item.porcentajeCentroCosto || 0),
        0
      )

      if (totalPorcentaje !== 100) {
        setCentroCostoError('La suma de porcentajes debe dar 100%')
        return
      }

      const ids = centroCostoPayload.map(item => item.idCentroCosto)
      if (new Set(ids).size !== ids.length) {
        setCentroCostoError('No puedes repetir centros de costo')
        return
      }
    }

    setLoading(true)

    try {
      const payload = {
        fecha,
        glosa,
        monto: Number(monto1),
        razonSocial,
        rut,
        nombreComprobante: selectedVinculacionLabel || null,
        cuentaContableId: selectedVinculacionPayloadId,
        extra: {
          cuentaInventarioCodigo:
            selectedInventoryAccount?.numCuenta || getCuentaOptionCode(selectedInventoryAccount),
          cuentaInventarioNombre: selectedVinculacionLabel || selectedInventoryAccount?.nameCuenta || '',
          cuentaInventarioMonto: Number(monto || 0),
          monto1,
          monto2
        }
      }

      const razonSeleccionada = razonesS?.find(r => r.rut === rut)
      const promises = []

      if (razonSeleccionada?.code) {
        const idRazonSocial = razonSeleccionada.code.split('-')[0]
        const payloadRazonSocial = { razonSocial }

        promises.push(
          organizationAPI.patchRazonSocial(currentBusiness.id, idRazonSocial, payloadRazonSocial)
        )
      }

      promises.push(organizationAPI.updateVinculacion(currentBusiness?.id, idLibro, payload))

      if (hasCentroCostoChanges) {
        promises.push(
          centroCostosAPI.createLibroCentroCosto(
            currentBusiness.id,
            idLibro,
            centroCostoPayload,
            temporalidad
          )
        )
      }

      await Promise.all(promises)
      setSwitchDate(true)
      functionGetData()
      handleClose()
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!fecha) return
    getDataBalanceRango(fecha)
  }, [fecha])

  useEffect(() => {
    if (!open) return
    getRazones()
    if (idLibro) functionGetData()
  }, [open, idLibro])

  useEffect(() => {
    getCentroCostos()
    getInventoryAccounts()
  }, [currentBusiness?.id])

  useEffect(() => {
    if (!open) return

    const initialRows = buildInitialCentroCostoRows(2)
    setCentrosCostoRows(initialRows)
    setCentroCostoTemporalidad(0)
    setInitialCentroTemporalidad(0)
    setInitialCentroCostoPayload(buildCentroCostoPayload(initialRows))
    setCentroCostoError('')
    setSelectedExistenciaCuenta('')
    setHasInitializedInventoryAccount(false)
    setLoadedLibroId(null)
  }, [open])

  useEffect(() => {
    if (data) {
      if (data.fecha) {
        const parsedDate = new Date(data.fecha)
        const formattedDate = parsedDate.toISOString().split('T')[0]
        setFecha(formattedDate)
      }

      if (data.fechaVencimiento) {
        const parsedDate = new Date(data.fechaVencimiento)
        const formattedDate = parsedDate.toISOString().split('T')[0]
        setVencimiento(formattedDate)
      }

      if (data.glosa) setGlosa(data.glosa)
      if (data.razonSocial) setRazonSocial(data.razonSocial)
      if (data.rut) setRut(data.rut)

      if (data.extra) {
        if (data.extra.monto1) setMonto1(data.extra.monto1)
        if (data.extra.monto2 !== undefined) setMonto2(data.extra.monto2)
      }

      if (Array.isArray(data.centro) && data.centro.length > 0) {
        const rows = data.centro.map((centro, index) => ({
          centroCostoId: centro.centroCostoId,
          porcentaje: centro.porcentajeCentroCosto,
          color: CENTER_COLORS[index % CENTER_COLORS.length]
        }))

        setCentrosCostoRows(rows)
        setInitialCentroCostoPayload(buildCentroCostoPayload(rows))
      } else if (open) {
        const initialRows = buildInitialCentroCostoRows(2)
        setCentrosCostoRows(initialRows)
        setInitialCentroCostoPayload(buildCentroCostoPayload(initialRows))
      }

      setCentroCostoTemporalidad(0)
      setInitialCentroTemporalidad(0)
    }
  }, [data, open])

  useEffect(() => {
    if (!open || hasInitializedInventoryAccount || inventoryAccountOptions.length === 0) return

    const hasLoadedLibroData = !idLibro || Number(loadedLibroId) === Number(idLibro)

    if (!hasLoadedLibroData) return

    const dataCuentaId = getCuentaDataId(data)
    if (dataCuentaId !== null && dataCuentaId !== undefined) {
      setSelectedExistenciaCuenta(dataCuentaId)
      setHasInitializedInventoryAccount(true)
      return
    }

    const matchedOption = inventoryAccountOptions.find(option => {
      const sameCode =
        data?.extra?.cuentaInventarioCodigo &&
        normalizeText(option.numCuenta) === normalizeText(data.extra.cuentaInventarioCodigo)
      const sameName =
        data?.extra?.cuentaInventarioNombre &&
        normalizeText(option.nameCuenta) === normalizeText(data.extra.cuentaInventarioNombre)

      return sameCode || sameName
    })

    if (matchedOption) {
      setSelectedExistenciaCuenta(getComparableCuentaId(matchedOption.value))
      setHasInitializedInventoryAccount(true)
      return
    }

    if (!selectedExistenciaCuenta) {
      setSelectedExistenciaCuenta(getComparableCuentaId(inventoryAccountOptions[0].value))
    }
    setHasInitializedInventoryAccount(true)
  }, [
    data,
    hasInitializedInventoryAccount,
    idLibro,
    inventoryAccountOptions,
    loadedLibroId,
    open,
    selectedExistenciaCuenta
  ])

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    setFecha(today)
    setVencimiento(today)
  }, [])

  useEffect(() => {
    if (!selectedInventoryAccount) {
      setMonto('')
      return
    }

    setMonto(String(selectedInventoryAccount.activo || 0))
  }, [selectedInventoryAccount])

  useEffect(() => {
    const currentMonto = Number(monto || 0)
    const adjustmentMonto = Number(monto1 || 0)

    if (monto1 === '' || monto1 === null) {
      setMonto2('')
      setMonto3('')
      return
    }

    setMonto2(currentMonto - adjustmentMonto)
    setMonto3(currentMonto - adjustmentMonto)
  }, [monto, monto1])

  const selectedInventoryLabel = selectedInventoryAccount
    ? `${selectedInventoryAccount.nameCuenta} ${selectedInventoryAccount.numCuenta}`
    : 'Existencia'

  const maxCentroRows = getAvailableCentroCostosCount()

  return (
    <ResizableModal
      zIndex={1400}
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton
            title='CANCELAR'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
          />
          <CustomButton
            title={
              action === 'replicar'
                ? 'REPLICAR VINCULACION'
                : action === 'modificar'
                  ? 'GRABAR VINCULACION'
                  : 'GRABAR VINCULACION'
            }
            color='accent2'
            className='rounded-full'
            type='button'
            disabled={loading || loadingInventoryAccounts || loadingBalance || Boolean(balanceError)}
            onClick={action === 'modificar' ? handleSubmit : handleDatos}
          />
        </>
      }
    >
      <>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
            <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
          </Box>
        ) : (
          <>
            <div className='mt-4'>
              <div className='flex justify-between items-center mb-6'>
                <div>
                  <Title className='text-3xl font-sans'>Ajuste de Existencia</Title>
                  {loadingBalance && <p role='status'>Cargando saldo de existencias...</p>}
                  {balanceError && <p role='alert'>{balanceError}</p>}
                </div>
              </div>
              <div className="flex w-full items-start h-full p-6 bg-white rounded-lg shadow-md'">
                <div className='flex flex-col gap-6 w-full'>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 w-full'>
                    <InputField label='Folio' value={folio} type='text' disabled />
                    <div>
                      <label className='block text-sm font-sans font-medium text-gray-700'>
                        Fecha
                      </label>
                      <input
                        type='date'
                        value={fecha}
                        onChange={e => setFecha(e.target.value)}
                        className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                      />
                    </div>
                    <div className='grid grid-cols-1 gap-4 col-span-2'>
                      <SelectField
                        label='Cuenta de existencia'
                        value={selectedExistenciaCuenta}
                        onChange={e =>
                          setSelectedExistenciaCuenta(getComparableCuentaId(e.target.value))
                        }
                        options={inventoryAccountOptions}
                        disabled={loadingInventoryAccounts}
                      />
                      <InputField
                        label={`Saldo actual (${selectedInventoryLabel})`}
                        placeholder='$'
                        type='text'
                        value={formatMonto(monto)}
                        onChange={() => {}}
                        disabled
                      />
                      <InputField disabled={bankAmountLocked}
                        label='Ajuste de Existencia a Costo de Venta'
                        placeholder='$'
                        type='text'
                        value={formatMonto(monto1)}
                        onChange={e => setMonto1(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                      <InputField
                        label={`Nuevo Inventario (${selectedInventoryLabel})`}
                        placeholder='$'
                        type='text'
                        value={monto2 === '' ? '' : formatMonto(monto2)}
                        onChange={() => {}}
                        disabled
                      />
                      <InputField disabled={bankAmountLocked}
                        label='Costo de Venta Aplicado'
                        placeholder='$'
                        type='text'
                        value={formatMonto(monto1)}
                        onChange={e => setMonto1(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                    </div>
                    <InputField
                      label='Clasificacion'
                      placeholder='3-1-2-1 Costo de Venta'
                      type='text'
                      disabled
                    />
                    <TextAreaField
                      label='Detalle'
                      placeholder='Describe en detalle...'
                      value={glosa}
                      onChange={e => setGlosa(e.target.value)}
                    />
                  </div>

                  {(action === 'modificar' || action === 'replicar') && (
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 w-full'>
                      <div className='md:col-span-1'>
                        <CircleProgress rows={centrosCostoRows} />
                      </div>
                      <div className='md:col-span-2'>
                        <div className='grid grid-cols-1 gap-4'>
                          <div className='relative flex flex-col'>
                            <label className='absolute -top-3 left-4 z-10 bg-white px-1 text-xs font-sans font-medium text-gray-500'>
                              Temporalidad centro costos
                            </label>
                            <select
                              className='h-9 w-full border border-gray-300 rounded-full px-4 text-xs font-sans'
                              value={centroCostoTemporalidad}
                              onChange={e => setCentroCostoTemporalidad(Number(e.target.value))}
                              disabled={data.validation === -5}
                            >
                              <option value={0}>Solo este movimiento</option>
                              <option value={1}>Este registro y todos los anteriores</option>
                              <option value={2}>Este registro y todos los posteriores</option>
                              <option value={3}>Aplicar a todos los registros</option>
                            </select>
                          </div>

                          <div className='flex flex-col gap-3'>
                            {centrosCostoRows.map((row, index) => (
                              <div
                                key={index}
                                className='grid grid-cols-[1fr_160px] gap-3 items-end'
                              >
                                <CustomSelect
                                  label='Centro de costo'
                                  name={`centroCosto-${index}`}
                                  options={getCentroCostoOptions()}
                                  value={row.centroCostoId}
                                  onChange={e =>
                                    handleCentroCostoRowChange(index, 'centroCostoId', e.target.value)
                                  }
                                  disabled={data.validation === -5}
                                />

                                <div>
                                  <label className='text-xs text-gray-500 font-sans'>
                                    Porcentaje
                                  </label>
                                  <div className='flex items-center gap-2'>
                                    <input
                                      type='number'
                                      min={0}
                                      max={100}
                                      className='h-9 w-full border border-gray-300 rounded-full px-3 text-xs'
                                      value={row.porcentaje === 0 ? '' : row.porcentaje}
                                      onChange={e =>
                                        handleCentroCostoRowChange(index, 'porcentaje', e.target.value)
                                      }
                                      disabled={data.validation === -5}
                                    />

                                    {centrosCostoRows.length > 0 && (
                                      <button
                                        type='button'
                                        onClick={() => removeCentroCostoRow(index)}
                                        className='text-red-500 hover:text-red-700'
                                        title='Eliminar centro de costo'
                                      >
                                        <ClearOutlinedIcon fontSize='small' />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className='flex w-full items-center justify-between'>
                              <button
                                type='button'
                                className={`text-xs ${
                                  maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-accent3'
                                }`}
                                onClick={addCentroCostoRow}
                                disabled={
                                  data.validation === -5 ||
                                  (maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows)
                                }
                              >
                                + Agregar centro de costo
                              </button>
                              <button
                                type='button'
                                className={`text-xs ${
                                  data.validation === -5 || centrosCostoRows.length === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-600'
                                }`}
                                onClick={clearCentroCostoRows}
                                disabled={data.validation === -5 || centrosCostoRows.length === 0}
                              >
                                - Quitar asignación
                              </button>
                            </div>
                          </div>

                          {centroCostoError && (
                            <p className='text-xs text-red-500 font-sans'>{centroCostoError}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </ResizableModal>
  )
}

export default AjusteExistencia
